import logger from '../../utils/logger';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  Eye, 
  ArrowLeft,
  Globe,
  Tag,
  Calendar,
  Image as ImageIcon,
  Upload,
  X
} from 'lucide-react';
import blogService from '../../services/blogService';
import toast from 'react-hot-toast';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';

// Rich Text Editor Component (we'll install react-quill)
import ReactQuill from 'react-quill';
import { sanitizeHtml } from '../../utils/security';
import 'react-quill/dist/quill.snow.css';

const BlogEditor = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [blog, setBlog] = useState({
    title: '',
    content: '',
    excerpt: '',
    featuredImageUrl: '',
    slug: '',
    status: 'DRAFT',
    category: 'GENERAL',
    featured: false,
    tags: [],
    metaTitle: '',
    metaDescription: ''
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Handle Quill editor image upload
  const handleQuillImageUpload = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/jpeg,image/jpg,image/png,image/gif,image/webp');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        try {
          setUploadingImage(true);
          
          const formData = new FormData();
          formData.append('file', file);
          formData.append('category', 'blog-content');

          const response = await fetch('/api/upload/image', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData
          });

          if (response.ok) {
            const result = await response.json();
            const imageUrl = result.url || result.data?.url;
            
            if (imageUrl) {
              // Insert image into editor
              const quill = document.querySelector('.ql-editor').__quill;
              const range = quill.getSelection();
              quill.insertEmbed(range.index, 'image', imageUrl);
              toast.success(t('admin.blogEditor.imageUploadAndInserted', 'Image uploaded and inserted!'));
            }
          } else {
            throw new Error('Failed to upload image');
          }
        } catch (error) {
          logger.error('Image upload error:', error);
          toast.error(t('admin.blogEditor.imageUploadFailed'));
        } finally {
          setUploadingImage(false);
        }
      }
    };
  };

  // Rich text editor configuration
  const quillModules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        [{ 'align': [] }],
        ['clean']
      ],
      handlers: {
        image: handleQuillImageUpload
      }
    },
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent',
    'blockquote', 'code-block', 'link', 'image', 'video', 'align'
  ];

  const loadBlog = useCallback(async () => {
    setLoading(true);
    try {
      const response = await blogService.getBlogById(id);
      if (response.success) {
        setBlog(response.data);
      } else {
        toast.error(t('admin.blogEditor.loadBlogFailed'));
        navigate('/admin/blog');
      }
    } catch (error) {
      toast.error(t('admin.blogEditor.loadBlogFailed'));
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    if (isEditing) {
      loadBlog();
    }
  }, [isEditing, loadBlog]);

  // loadBlog memoized above

  const generateSlug = useCallback((title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .trim();
  }, []);

  const handleTitleChange = (title) => {
    setBlog(prev => ({
      ...prev,
      title,
      slug: generateSlug(title),
      metaTitle: title || prev.metaTitle
    }));
  };

  const validateForm = (blogData) => {
    const errors = {};
    
    if (!blogData.title || blogData.title.trim() === '') {
      errors.title = t('admin.titleRequired', 'Blog title is required.');
    }
    
    if (!blogData.content || blogData.content.trim() === '' || blogData.content === '<p><br></p>') {
      errors.content = t('admin.contentRequired', 'Blog content is required.');
    }
    
    return errors;
  };

  const handleSave = async (status = blog.status) => {
    const blogToSave = { ...blog, status };
    const errors = validateForm(blogToSave);
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      toast.error(t('admin.blogEditor.fixValidationErrors'));
      return;
    }

    setSaving(true);
    try {
      const sanitizedContent = sanitizeHtml(blogToSave.content);
      const blogPayload = { ...blogToSave, content: sanitizedContent };
      let response;
      if (isEditing) {
        response = await blogService.updateBlog(id, blogPayload);
      } else {
        response = await blogService.createBlog(blogPayload);
      }

      if (response.success) {
        toast.success(isEditing ? t('admin.blogUpdated', 'Blog updated successfully!') : t('admin.blogCreated', 'Blog created successfully!'));
        navigate('/admin/blog');
      } else {
        toast.error(response.error || t('admin.blogEditor.saveFailed'));
      }
    } catch (error) {
      toast.error(t('admin.blogEditor.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    if (!blog.title || !blog.content) {
      toast.error(t('admin.blogEditor.addTitleContent'));
      return;
    }
    
    // Create a temporary preview URL
    const previewData = {
      ...blog,
      id: 'preview',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      viewCount: 0
    };
    
    // Store in sessionStorage for preview
    sessionStorage.setItem('blogPreview', JSON.stringify(previewData));
    
    // Open preview in new tab
    window.open('/blog/preview', '_blank');
  };

  const handleTagsChange = (tagsString) => {
    const tags = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);
    setBlog(prev => ({ ...prev, tags }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('admin.blogEditor.pleaseSelectValidImage'));
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error(t('admin.blogEditor.imageSizeLimit'));
      return;
    }

    setUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'blog');

      const response = await fetch('/api/upload/image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        const imageUrl = result.url || result.data?.url;
        
        if (imageUrl) {
          setBlog(prev => ({ ...prev, featuredImageUrl: imageUrl }));
          toast.success(t('admin.blogEditor.imageUploaded', 'Image uploaded successfully!'));
        } else {
          throw new Error('No image URL returned from server');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload image');
      }
    } catch (error) {
  logger.error('Image upload error:', error);
      toast.error(error.message || t('admin.blogEditor.imageUploadFailed'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset the input so the same file can be selected again if needed
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removeImage = () => {
    setBlog(prev => ({ ...prev, featuredImageUrl: '' }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">{t('admin.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gray-800/95 backdrop-blur-sm border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin/blog')}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('admin.blogEditor.backToBlogList', 'Back to Blog Management')}
              </Button>
              <div className="h-6 w-px bg-gray-600"></div>
              <h1 className="text-xl font-semibold text-white">
                {isEditing ? t('admin.editBlog', 'Edit Blog Post') : t('admin.createBlog', 'Create New Blog Post')}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={handlePreview}
                variant="outline"
                size="sm"
                disabled={!blog.title || !blog.content}
              >
                <Eye className="w-4 h-4 mr-2" />
                {t('admin.blogEditor.preview', 'Preview')}
              </Button>
              
              <Button
                onClick={() => handleSave('DRAFT')}
                variant="outline"
                size="sm"
                disabled={saving}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? t('admin.blogEditor.saving', 'Saving...') : t('admin.blogEditor.saveDraft', 'Save as Draft')}
              </Button>
              
              <Button
                onClick={() => handleSave('PUBLISHED')}
                size="sm"
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                <Globe className="w-4 h-4 mr-2" />
                Publish
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <Card>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('admin.title', 'Title')} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={blog.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    className={`w-full bg-gray-700 border rounded-lg px-4 py-3 text-white text-lg focus:outline-none focus:border-[#a15507] ${
                      validationErrors.title ? 'border-red-400' : 'border-gray-600'
                    }`}
                    placeholder={t('admin.enterTitle', 'Enter your blog post title...')}
                  />
                  {validationErrors.title && (
                    <p className="text-red-400 text-sm mt-1">{validationErrors.title}</p>
                  )}
                </div>

                {/* Auto-generated slug preview */}
                {blog.slug && (
                  <div className="text-sm text-gray-400">
                    URL: <span className="text-[#a15507]">/{blog.slug}</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Content Editor */}
            <Card>
              <div className="space-y-4">
                <label className="block text-white text-sm font-medium">
                  {t('admin.content', 'Content')} <span className="text-red-400">*</span>
                </label>
                <div className="quill-editor">
                  <ReactQuill
                    value={blog.content}
                    onChange={(content) => setBlog(prev => ({ ...prev, content }))}
                    modules={quillModules}
                    formats={quillFormats}
                    theme="snow"
                    placeholder={t('admin.enterContent', 'Start writing your blog post content...')}
                    style={{
                      backgroundColor: '#374151',
                      borderRadius: '0.5rem',
                      minHeight: '400px'
                    }}
                  />
                </div>
                {validationErrors.content && (
                  <p className="text-red-400 text-sm mt-1">{validationErrors.content}</p>
                )}
              </div>
            </Card>

            {/* Excerpt */}
            <Card>
              <div className="space-y-4">
                <label className="block text-white text-sm font-medium">
                  {t('admin.excerpt', 'Excerpt')}
                  <span className="text-gray-400 text-xs ml-2">({t('admin.blogEditor.excerptDescription', 'Optional - Auto-generated if empty')})</span>
                </label>
                <textarea
                  value={blog.excerpt}
                  onChange={(e) => setBlog(prev => ({ ...prev, excerpt: e.target.value }))}
                  rows={3}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#a15507] resize-vertical"
                  placeholder={t('admin.blogEditor.excerptPlaceholder', 'Brief summary of your blog post...')}
                />
                <div className="text-xs text-gray-400">
                  {blog.excerpt.length}/500 {t('common.characters', 'characters')}
                </div>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publishing Options */}
            <Card>
              <h3 className="text-white font-medium mb-4 flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {t('common.publishing', 'Publishing')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">{t('admin.status', 'Status')}</label>
                  <select
                    value={blog.status}
                    onChange={(e) => setBlog(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#a15507]"
                  >
                    <option value="DRAFT">{t('admin.blogStatus.draft', 'Draft')}</option>
                    <option value="PUBLISHED">{t('admin.blogStatus.published', 'Published')}</option>
                    <option value="ARCHIVED">{t('admin.blogStatus.archived', 'Archived')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">{t('admin.category', 'Category')}</label>
                  <select
                    value={blog.category}
                    onChange={(e) => setBlog(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#a15507]"
                  >
                    <option value="GENERAL">{t('common.general', 'General')}</option>
                    <option value="COFFEE_FACTS">{t('common.coffeeFacts', 'Coffee Facts')}</option>
                    <option value="RECIPES">{t('common.recipes', 'Recipes')}</option>
                    <option value="TIPS">{t('common.tips', 'Tips')}</option>
                    <option value="REVIEWS">{t('common.reviews', 'Reviews')}</option>
                    <option value="NEWS">{t('common.news', 'News')}</option>
                    <option value="LIFESTYLE">{t('common.lifestyle', 'Lifestyle')}</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={blog.featured}
                    onChange={(e) => setBlog(prev => ({ ...prev, featured: e.target.checked }))}
                    className="rounded border-gray-600 bg-gray-700 text-[#a15507] focus:ring-[#a15507]"
                  />
                  <label htmlFor="featured" className="text-white text-sm">
                    Featured Post
                  </label>
                </div>
              </div>
            </Card>

            {/* Featured Image */}
            <Card>
              <h3 className="text-white font-medium mb-4 flex items-center">
                <ImageIcon className="w-4 h-4 mr-2" />
                {t('admin.blogEditor.featuredImage', 'Featured Image')}
              </h3>
              <div className="space-y-4">
                {/* Image Upload Area */}
                {!blog.featuredImageUrl ? (
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center hover:border-[#a15507] transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="flex flex-col items-center">
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-white text-sm mb-1">
                        {uploadingImage ? t('admin.blogEditor.uploadingImage', 'Uploading...') : t('admin.blogEditor.dragDropImage', 'Click to upload or drag & drop')}
                      </p>
                      <p className="text-gray-400 text-xs">
                        JPEG, PNG, GIF or WebP (max 5MB)
                      </p>
                    </div>
                    {uploadingImage && (
                      <div className="mt-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#a15507] mx-auto"></div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={blog.featuredImageUrl}
                      alt="Featured"
                      className="w-full h-40 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <button
                      onClick={removeImage}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition-colors"
                      title="Remove image"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-2 right-2 bg-[#a15507] text-white rounded-lg px-3 py-1 text-sm hover:bg-[#924e06] transition-colors"
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? t('admin.blogEditor.uploadingImage', 'Uploading...') : t('admin.blogEditor.changeImage', 'Change')}
                    </button>
                  </div>
                )}
                
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {/* Manual URL Input (Alternative) */}
                <div className="pt-4 border-t border-gray-700">
                  <label className="block text-gray-400 text-xs mb-2">
                    {t('admin.blogEditor.orPasteImageUrl', 'Or paste image URL:')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={blog.featuredImageUrl}
                      onChange={(e) => setBlog(prev => ({ ...prev, featuredImageUrl: e.target.value }))}
                      className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#a15507]"
                      placeholder={t('admin.blogEditor.placeholders.imageUrl')}
                    />
                    {blog.featuredImageUrl && (
                      <button
                        onClick={removeImage}
                        className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors text-sm"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Tags */}
            <Card>
              <h3 className="text-white font-medium mb-4 flex items-center">
                <Tag className="w-4 h-4 mr-2" />
                {t('admin.tags', 'Tags')}
              </h3>
              <div className="space-y-4">
                <input
                  type="text"
                  value={blog.tags?.join(', ') || ''}
                  onChange={(e) => handleTagsChange(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#a15507]"
                  placeholder={t('admin.blogEditor.placeholders.tags')}
                />
                <div className="text-xs text-gray-400">
                  {t('admin.blogEditor.separateTagsWithCommas', 'Separate tags with commas')}
                </div>
                {blog.tags && blog.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {blog.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-[#a15507] text-white text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            {/* SEO */}
            <Card>
              <h3 className="text-white font-medium mb-4 flex items-center">
                <Globe className="w-4 h-4 mr-2" />
                {t('admin.blogEditor.seoSettings', 'SEO Settings')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('admin.blogEditor.metaTitle', 'Meta Title')}
                    <span className="text-gray-400 text-xs ml-2">({t('admin.blogEditor.autoGeneratedIfEmpty', 'Auto-generated if empty')})</span>
                  </label>
                  <input
                    type="text"
                    value={blog.metaTitle}
                    onChange={(e) => setBlog(prev => ({ ...prev, metaTitle: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#a15507]"
                    placeholder={t('admin.blogEditor.placeholders.metaTitle')}
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    {t('admin.blogEditor.metaDescription', 'Meta Description')}
                    <span className="text-gray-400 text-xs ml-2">({t('admin.blogEditor.autoGeneratedIfEmpty', 'Auto-generated if empty')})</span>
                  </label>
                  <textarea
                    value={blog.metaDescription}
                    onChange={(e) => setBlog(prev => ({ ...prev, metaDescription: e.target.value }))}
                    rows={3}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#a15507] resize-vertical"
                    placeholder={t('admin.blogEditor.placeholders.metaDescription')}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Custom CSS for Quill Editor */}
      <style jsx global>{`
        .quill-editor .ql-editor {
          background-color: #374151 !important;
          color: white !important;
          border-radius: 0.5rem;
          min-height: 400px;
        }
        
        .quill-editor .ql-toolbar {
          background-color: #4B5563 !important;
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          border-bottom: 1px solid #6B7280;
        }
        
        .quill-editor .ql-toolbar .ql-stroke {
          stroke: #D1D5DB !important;
        }
        
        .quill-editor .ql-toolbar .ql-fill {
          fill: #D1D5DB !important;
        }
        
        .quill-editor .ql-toolbar button:hover {
          background-color: #6B7280 !important;
        }
        
        .quill-editor .ql-container {
          border-bottom-left-radius: 0.5rem;
          border-bottom-right-radius: 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default BlogEditor;
